// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSessionStore from '@/app/store/session';
import actionsDataTable from '../actions/actionsDataTable';
// Components
import ModalSendMail from '../../modal/components/modals/ModalSendMail';
import Entity from '../../entity/Entity';
// Common
import genUID from '@/common-utils/functions/generateUniqueId';
import { buildUrl, extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import { useToast } from '@/common-utils/hooks/use-toast';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.exportApi
 * @param root0.uuidToExport
 * @param root0.queryFilters
 * @param root0.customExportQuery
 * @returns {JSX.Element} ModalExport
*/
function ModalExport({ isOpen, setIsOpen, exportApi, uuidToExport, queryFilters, customExportQuery }) {
    // Constants
    const MAX_EMAILS_TO_SEND = 5;

    // Toast
    const { toast } = useToast();

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const session = useSessionStore(s => s.session);

    // State
    const [emailsList, setEmailsList] = useState([{ id: genUID(), email: session.email ?? '', isValid: true }]);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.export.sending_confirmation' />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.export.error' />,
            variant: 'danger'
        });
    };
    const submitExport = actionsDataTable.exportTable(onSuccess, onError);

    return (
        <ModalSendMail
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            renderList={emailsList}
            setRenderList={setEmailsList}
            onValidate={() => {
                // Prepare data to send
                const recipients = emailsList.map((item) => item.email).join();
                // Extract query filters
                const urlQueries = buildUrl('', { filters: queryFilters });
                const queries = extractQueriesFromUrl(urlQueries);

                submitExport.mutate({
                    exportApi,
                    uuid: uuidToExport,
                    pathParams: {
                        ...queries,
                        ...customExportQuery,
                        provider: rootOrganization.uuid,
                        recipients
                    }
                });
            }}
            isLoading={submitExport.isLoading}
            header={{ title: 'modal.export.title' }}
            message={{ entity: 'modal.export.emails' }}
            linkValidity={{ entity: 'modal.export.link_validity' }}
            confirm={{ entity: `modal.send_mail.confirm.${emailsList.length > 1 ? 'multiple' : 'single'}` }}
            maxEmailsNumber={MAX_EMAILS_TO_SEND}
        />
    );
}

ModalExport.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    exportApi: PropTypes.object,
    uuidToExport: PropTypes.string,
    queryFilters: PropTypes.array,
    customExportQuery: PropTypes.object
};

export default ModalExport;